"use client"

import { DollarSignIcon, EuroIcon } from "lucide-react"
import { NumericFormat, type NumberFormatValues, type SourceInfo } from "react-number-format"

import { currencyScale } from "@ibada/shared-types/finance"
import { FinanceCurrency } from "@/features/finance/model/finance-types"
import { getCurrencyPrefix } from "@/features/finance/lib/finance-utils"
import { useLocale } from "@/i18n"
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@ibada/ui"

function renderCurrencyPrefix(currencyCode: string) {
  switch (currencyCode) {
    case FinanceCurrency.USD:
      return <DollarSignIcon className="size-4" aria-hidden="true" />
    case FinanceCurrency.EUR:
      return <EuroIcon className="size-4" aria-hidden="true" />
    default:
      return getCurrencyPrefix(currencyCode)
  }
}

/**
 * The grouping and decimal characters the active locale uses, read from `Intl` rather than hardcoded: fr groups
 * thousands with a narrow no-break space and uses a comma for decimals, en uses a comma and a dot. `11111.1`
 * exposes both a `group` and a `decimal` part in one format pass.
 */
function localeSeparators(locale: string): { thousandSeparator: string; decimalSeparator: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1)
  return {
    thousandSeparator: parts.find((part) => part.type === "group")?.value ?? ",",
    decimalSeparator: parts.find((part) => part.type === "decimal")?.value ?? ".",
  }
}

type CurrencyAmountInputProps = {
  currencyCode: string
  value: number | string | undefined
  onChange: (value: string) => void
  disabled?: boolean
  /** Locked but fully legible (no dimming) — for a computed/derived amount the user should still read clearly. */
  readOnly?: boolean
  /** Retained for call-site compatibility; the allowed range is now expressed via `allowNegative`/`decimalScale`. */
  min?: string
  /** Retained for call-site compatibility; decimal precision is now driven by the currency's scale. */
  step?: string
  id?: string
  name?: string
  onBlur?: () => void
  /** What an empty field shows. A bare `0` reads as "nothing allocated yet" without pretending to be a value. */
  placeholder?: string
  /** Classes for the input itself (not the group) — e.g. right-aligned tabular figures in a column of amounts. */
  inputClassName?: string
}

export function CurrencyAmountInput({
  currencyCode,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  id,
  name,
  onBlur,
  placeholder,
  inputClassName,
}: CurrencyAmountInputProps) {
  const locale = useLocale()
  const { thousandSeparator, decimalSeparator } = localeSeparators(locale)

  // A NUMERIC zero is a seeded default ("no amount yet"), so it shows as an empty field rather than a `0` the
  // operator has to delete first. A STRING is the canonical value the operator is typing (dot-decimal) and is
  // passed through verbatim — blanking `"0"` here would swallow the first keystroke of `0.50` (IBA-160).
  const displayValue =
    value == null ? ""
    : typeof value === "number" ? (value === 0 ? "" : String(value))
    : value

  return (
    <InputGroup>
      <InputGroupAddon align="inline-start">
        <InputGroupText>{renderCurrencyPrefix(currencyCode)}</InputGroupText>
      </InputGroupAddon>
      <NumericFormat
        customInput={InputGroupInput}
        id={id}
        name={name}
        placeholder={placeholder}
        className={inputClassName}
        // The value is the canonical dot-decimal money string; `valueIsNumericString` tells NumericFormat to read
        // it as such and reformat it for display with the locale separators above.
        value={displayValue}
        valueIsNumericString
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        // RWF has no minor unit (scale 0) so it refuses a decimal point entirely; USD/EUR allow two places. No
        // `fixedDecimalScale`: forcing trailing zeros while the operator is still typing fights their keystrokes.
        decimalScale={currencyScale(currencyCode)}
        allowNegative={false}
        inputMode="decimal"
        disabled={disabled}
        readOnly={readOnly}
        onBlur={onBlur}
        onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) => {
          // `values.value` is the raw dot-decimal string with NO separators — exactly the ledger's wire format.
          // The `source === "event"` guard only forwards real user keystrokes: reacting to reformats driven by
          // the controlled `value` prop would create a feedback loop and break mid-decimal typing.
          if (sourceInfo.source === "event") onChange(values.value)
        }}
      />
    </InputGroup>
  )
}
